import React, { createContext, useReducer, useEffect, useContext, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useQueryParams, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { MY_SCHEDULE_TAB, FULL_SCHEDULE_TAB } from '@fs/zion-rootstech'
import useScheduleData from './hooks/useScheduleData'
import { useDetailAugmentation } from '../../hooks'
import { isInMySchedule, mapToMyScheduleDate } from './helpers/scheduleHelpers'
import { useMyScheduleContext } from '../../providers'

/**
 * ScheduleProvider
 *
 * Route-level provider for the full schedule view.
 * Manages display state (day/type filters) and augments schedule data with user's selections.
 *
 * Responsibilities:
 * - Fetch and display full schedule data
 * - Manage filter state (day selection, type filtering)
 * - Augment schedule items with inMySchedule flags from MyScheduleProvider
 * - Group sessions by day for display
 * - URL sync for filters
 *
 * NOTE: For add/remove operations, use useMyScheduleContext() directly.
 */

const ScheduleContext = createContext()
export const useScheduleContext = () => useContext(ScheduleContext)

const actionTypes = {
  SET_DAY: 'SET_DAY',
  SET_TYPE: 'SET_TYPE',
  SET_SCHEDULE: 'SET_SCHEDULE',
}

const initialState = {
  day: '',
  type: 'allsessions',
  schedule: null,
}

function reducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_DAY: {
      return {
        ...state,
        day: action.value,
      }
    }
    case actionTypes.SET_TYPE: {
      return {
        ...state,
        type: action.value,
      }
    }
    case actionTypes.SET_SCHEDULE: {
      const scheduleDayKeys = Object.keys(action.value)
      return {
        ...state,
        schedule: action.value,
        day: state.day || scheduleDayKeys[0],
      }
    }
    default: {
      return initialState
    }
  }
}

/**
 * Groups schedule items by day (yyyy-mm-dd format)
 */
const groupByDay = (schedule) => {
  const scheduleGroupedIntoDays = {}
  schedule.forEach((item) => {
    const sessionDate = new Date(item.date)
    const dayId = mapToMyScheduleDate(sessionDate, true)
    scheduleGroupedIntoDays[dayId] = scheduleGroupedIntoDays[dayId] || []
    scheduleGroupedIntoDays[dayId].push(item)
  })
  return scheduleGroupedIntoDays
}

/**
 * Augments schedule items with inMySchedule flag based on user's saved schedule
 */
const addMyScheduleProp = (mySchedule, schedule) => {
  const scheduleDateKeys = Object.keys(schedule)
  const updatedSchedule = {}

  scheduleDateKeys.forEach((dateKey) => {
    schedule[dateKey].forEach((scheduleItem) => {
      const { date, item } = scheduleItem

      updatedSchedule[dateKey] = updatedSchedule[dateKey] || []
      updatedSchedule[dateKey].push({
        ...scheduleItem,
        item: { ...scheduleItem.item, inMySchedule: isInMySchedule(mySchedule, item.contentId, date) },
      })
    })
  })
  return updatedSchedule
}

/**
 * Removes 'tab' from query params (now derived from pathname)
 */
const omitTabFromQuery = (queryObj) => {
  const result = { ...queryObj }
  delete result.tab
  return result
}

export default function ScheduleProvider({ children }) {
  const query = useQueryParams()
  const history = useHistory()
  const location = useLocation()
  const user = useUser()

  // Get mySchedule from app-level provider
  const { mySchedule } = useMyScheduleContext()

  // Exclude 'tab' from query params since it's now derived from pathname
  const initialQuery = omitTabFromQuery(query.query)
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...initialQuery })

  // Derive tab based on current route
  const tab = location.pathname.includes('/user/schedule') ? MY_SCHEDULE_TAB : FULL_SCHEDULE_TAB

  const { loading: scheduleLoading, data } = useScheduleData()
  const transformedData = useMemo(() => {
    return data.map(({ date, item }) => ({ ...item, date }))
  }, [data])

  const { data: augmentedData, loading } = useDetailAugmentation({
    loading: scheduleLoading,
    data: transformedData,
  })

  const formattedScheduleData = useMemo(() => {
    return augmentedData.map((item) => ({ item, date: item.date }))
  }, [augmentedData])

  const changeDay = useCallback(
    (value) => {
      dispatch({ type: actionTypes.SET_DAY, value })
      if (Object.keys(query.query).length === 0) {
        // Allows history navigation to don't skip previous page
        history.replace(`${location.pathname}?day=${value}`)
      } else {
        query.setQuery({ ...omitTabFromQuery(query.query), day: value })
      }
    },
    [query, history, location.pathname]
  )

  const changeType = useCallback(
    (value) => {
      dispatch({ type: actionTypes.SET_TYPE, value })
      query.setQuery({ ...omitTabFromQuery(query.query), type: value })
    },
    [query]
  )

  // Process schedule data and augment with mySchedule flags
  useEffect(() => {
    if (formattedScheduleData.length) {
      // Divide up the sessions by day
      const scheduleGroupedByDays = groupByDay(formattedScheduleData)

      if (user?.signedIn && mySchedule) {
        // Augment with inMySchedule flags from MyScheduleProvider
        const updatedSchedule = addMyScheduleProp(mySchedule, scheduleGroupedByDays)
        dispatch({ type: actionTypes.SET_SCHEDULE, value: updatedSchedule })
      } else {
        dispatch({ type: actionTypes.SET_SCHEDULE, value: scheduleGroupedByDays })
      }
    } else {
      dispatch({ type: actionTypes.SET_SCHEDULE, value: {} })
    }
  }, [formattedScheduleData, mySchedule, user])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      ...state,
      tab,
      changeDay,
      changeType,
      loading,
    }),
    [state, tab, changeDay, changeType, loading]
  )

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
}

ScheduleProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
